import { useEffect, useMemo, useState } from "react";
import useRouter from "@stadium/hooks/lib/use-custom-route";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";

import { useLazyQuery } from "@stadium/services/apollo-client";

import { useAuth0V2 } from "@stadium/services/auth0";
import { removeQueryParams } from "@stadium/helpers/routes";
import { useIsAuthGraphQLReady } from "@stadium/services/auth/client";

import { usePreviewModeContext } from "@contexts/preview-mode-context";
import { useSendItemsContext } from "@contexts/send-items-context";

import {
  getGlobalCountryZoneId,
  selectAllCountries,
} from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";

import { fetchExternalStore } from "@graphql/queries/rest-api/stores/basic-details";

import { InternalButton } from "@sharedComponents/buttons";

import usePopulateItems from "@hooks/cart/use-populate-items";
import { useWindowStorage } from "@hooks/use-window-storage";
import useFindOrCreateDraftOrder from "@hooks/cart/use-find-or-create-draft-order";
import useEmptyCart from "@hooks/cart/use-empty-cart";
import useCountryChange from "@hooks/use-country-change";

import { getIsLockerShop, getStoreExternalId } from "@selectors/store";
import { getCountry } from "@selectors/country";
import { getStorePreviewModeCartKey } from "@helpers/store";
import { useStoreContext } from "@components/store/provider";
import { updateOrderDetails, cumulativeCart } from "@actions/order";
import { setStoreBalance } from "@slices/store";

import { SM_PROGRAM_ID } from "@constants/app-defaults";
import { KIT_COUNTRIES, GLOBAL_COUNTRY } from "@constants/country";
import { SWAG_PURCHASED_FROM_US_INTENT } from "@constants/orders/cart";

import style from "./purge-main.module.scss";

const useGuestUserLogin = () => {
  const dispatch = useDispatch();

  const router = useRouter();
  const { editProductIds } = router.query;

  const isAuthGraphQLReady = useIsAuthGraphQLReady();

  const { isAuthenticated } = useAuth0V2();
  const { storeNumber, isKitShop, isKitRedemption, countryCode } = useStoreContext();
  const { previewMode } = usePreviewModeContext();
  const { syncOrders } = usePopulateItems();

  const { findOrCreateDraftOrder } = useFindOrCreateDraftOrder();
  const { emptyCart } = useEmptyCart({ showToast: false });

  const { isSendToMyself } = useSendItemsContext();

  const [localOrder, , resetCart] = useWindowStorage(
    getStorePreviewModeCartKey(storeNumber, countryCode, isSendToMyself),
  );

  const allCountries = useConfig(selectAllCountries);
  const globalCountryZoneId = useConfig(getGlobalCountryZoneId);
  const isLockerShop = useSelector(getIsLockerShop);
  const selectedCountry = useSelector(getCountry);
  const storeExternalId = useSelector(getStoreExternalId);

  const programIntent = isLockerShop ? SWAG_PURCHASED_FROM_US_INTENT : undefined;

  const [fetchStore] = useLazyQuery(fetchExternalStore, {
    variables: { storeId: storeExternalId, programIntent },
    skip: !storeExternalId,
    fetchPolicy: "cache-and-network",
  });

  const countries = useMemo(() => {
    if (isLockerShop || !isSendToMyself)
      return [{ ...GLOBAL_COUNTRY, zoneId: globalCountryZoneId }, ...allCountries];
    if (isKitShop && !isKitRedemption) return KIT_COUNTRIES;
    return allCountries;
  }, [isKitShop, isLockerShop, isSendToMyself, globalCountryZoneId]);

  const { changeCountry } = useCountryChange(countries);

  const [isOpen, setIsOpen] = useState(false);
  const [order, setOrder] = useState();
  const [loading, setLoading] = useState(false);
  const [orderCountry, setOrderCountry] = useState("");
  const [sendToManyStatus, setSendToManyStatus] = useState(null);

  useEffect(() => {
    if (!isAuthGraphQLReady) return;
    if (
      !isLockerShop &&
      !isKitShop &&
      localOrder?.itemCount &&
      isAuthenticated &&
      !previewMode &&
      storeExternalId &&
      selectedCountry.id
    ) {
      setSendToManyStatus(!isSendToMyself);
      if (editProductIds) {
        removeQueryParams({ queryKeys: ["editProductIds"], router });
      }
      fetchStore()
        .then(({ data }) => {
          if (data?.externalStore) {
            const cartPayload = data.externalStore.order;
            const storePayload = {
              balance: data.externalStore.availableStoreSpecificPoints,
              initBalance: data.externalStore.availableStoreSpecificPoints,
              isStoreOrderFetched: true,
            };

            dispatch(setStoreBalance(storePayload));
            if (cartPayload?.id) {
              return dispatch(cumulativeCart(cartPayload.id));
            }
            return findOrCreateDraftOrder();
          }
        })
        .then((order) => {
          if (order?.itemCount) {
            setIsOpen(true);
            setOrder(order);
            const country = allCountries.find(({ id }) => id === order.countryId);
            setOrderCountry(country);
          } else {
            syncOrders(localOrder, order).then(resetCart);
          }
        })
        .catch((err) => {
          throw err;
        });
    }
  }, [
    isAuthenticated,
    storeExternalId,
    selectedCountry.id,
    isLockerShop,
    isKitShop,
    isAuthGraphQLReady,
  ]);

  const keepNewZoneProducts = () => {
    setLoading(true);
    dispatch(
      updateOrderDetails(order.id, {
        program_id: SM_PROGRAM_ID,
        country_id: selectedCountry.id,
        send_to_many: sendToManyStatus,
        ...(sendToManyStatus && {
          gift_detail_attributes: {},
          treat_name: order.id,
        }),
      }),
    )
      .then(() => emptyCart(order.id))
      .then((order) => syncOrders(localOrder, order))
      .then(() => {
        resetCart();
        setLoading(false);
        setIsOpen(false);
      });
  };

  const keepExistingZoneProducts = () => {
    changeCountry(orderCountry);
    resetCart();
    setIsOpen(false);
  };

  const renderCartMergeConfirmationModal = () => {
    return (
      <Modal
        data-acsb-overlay="popup"
        show={true}
        animation={false}
        className={`${style.generalModal} ${style.confirmModal}`}
        dialogClassName={style.modalDialog}
      >
        <Modal.Body className={style.modalBody}>
          <p className={style.pTag}>
            Your cart already have items, updating cart will remove the products available in the
            cart right now.
          </p>
        </Modal.Body>
        <Modal.Footer className={style.modalFooter}>
          <InternalButton
            className={style.actionButton}
            onClick={keepExistingZoneProducts}
            styleType="primary-outline"
          >
            Keep Existing Cart
          </InternalButton>
          <InternalButton
            className={style.actionButton}
            isLoading={loading}
            onClick={keepNewZoneProducts}
            styleType="primary"
          >
            Update Cart
          </InternalButton>
        </Modal.Footer>
      </Modal>
    );
  };

  return { renderCartMergeConfirmationModal, isOpen };
};

export default useGuestUserLogin;
